<template>
    
    <div v-bind="$attrs">
        
    <v-autocomplete
        v-model="model"
        @blur="onBlur"
        @update:menu="onUpdateMenu"
        :items="localCollection"
        :loading="isLoading || props.loading"
        :search-input.sync="textSearch"
        :return-object="returnObject"
        :hide-selected="hideSelected"
        :hide-no-data="hideData"
        :item-text="itemText"
        :item-value="itemValue"
        :label="label"
        :clearable="clearable"
        :disabled="disabled"
        :multiple="multiple"
        :placeholder="placeholder"
        :outlined="outlined"
        :dense="dense"
        :error-messages="errorMessages"
        :hide-details="hideDetails"
        :small-chips="smallChips"
        class="custom-placeholder"
        >

          <template v-if="multiple && showCheckbox" v-slot:item="{ item, attrs, on }">
               <v-list-item v-bind="attrs" v-on="on">
                   <v-list-item-action>
                       <!-- Vuetify injeta o estado selecionado em attrs.inputValue -->
                       <v-simple-checkbox
                         :value="!!attrs.inputValue"
                         @click.stop="on && on.click && on.click($event)"
                       />
                   </v-list-item-action>
                   <v-list-item-content>
                       <v-list-item-title>{{ getItemText(item) }}</v-list-item-title>
                   </v-list-item-content>
               </v-list-item>
           </template>

        <template v-slot:no-data>
            <v-card flat>
                <v-card-text class="py-0 px-2 text-caption">
                    <span>Buscando por <strong class="indigo--text">{{ textSearch }}</strong>, não foi encontrado.</span>
                </v-card-text>
            </v-card>
            
        </template>

        <template v-slot:append-outer>
            <slot name="append-outer"></slot>
        </template>

        <template v-slot:prepend-item>
            <v-list-item @mousedown.prevent @click="toggleSelectAll" v-if="showSelectAll && multiple">
                <v-list-item-action>
                    <v-icon>{{  iconSelectAll() }}</v-icon>
                </v-list-item-action>
                <v-list-item-content>
                    <v-list-item-title>
                        Selecionar todos
                    </v-list-item-title>
                </v-list-item-content>
            </v-list-item>
            <v-divider class="mt-2" v-if="showSelectAll && multiple"></v-divider>
        </template>

        <template v-slot:append>
            
            <v-tooltip bottom v-if="showAdd">
                <template v-slot:activator="{ on, attrs }">
                    <v-btn v-bind="attrs" v-on="on" small icon @click="openDialogCreate">
                        <v-icon btn color="green darken-1">mdi-plus</v-icon>
                    </v-btn>
                </template>
                <span>Novo</span>
            </v-tooltip>

            <slot name="append"></slot>

        </template>

        <!-- Tratando o slot que mostra o texto do campo -->
        <template v-slot:selection="{ item, index }">

            <span v-if="index === 0">{{ getItemText(item) }}</span>
            <span v-if="index === 1" class="grey--text caption ml-1">(+{{ model.length - 1 }} outros)</span>

        </template>

    </v-autocomplete>
</div>
</template>

<script setup>

import { watch, onMounted, defineEmits, defineProps, ref, computed, onBeforeUpdate } from 'vue'
import { useStore } from '@/composables/useStore'
import debounce from 'lodash/debounce'

//Events
const emit = defineEmits(['input', 'update:search', 'onShowFormCreate', 'update:loading', 'update:item', 'change', 'blur', 'menu-closed'])

//Props
const props = defineProps({
        //Propriedades de dados
        value: { type: [String, Number, Object, Array] },
        item: { type: [Object, Array], required: false },
        errorMessages: { type: Array, required: false },
        columnOrder: { type: String, default: 'name' },
        columns: { type: String, default: 'id,name' },
        with: { type: String, required: false },

        //Propriedades de eventos
        clearable: { type: Boolean, required: false },
        disabled: { type: Boolean, required: false },
        multiple: { type: Boolean, required: false },

        //Propriedades de configuração
        itemText: { type: [String, Function], default: 'name' },
        itemValue: { type: String, default: 'id' },
        label: { type: String, required: false },
        placeholder: { type: String, default: "Comece a digitar para pesquisar"},
        outlined: { type: Boolean, required: false },
        dense: { type: Boolean, required: false },
        hideDetails: { type: [Boolean, String], default: 'auto' },
        smallChips: { type: Boolean, required: false },
        showAdd: { type: Boolean, required: false },
        showSelectAll: { type: Boolean, required: false },
        hideSelected: { type: Boolean, required: false },
        returnObject: { type: Boolean, required: false },
         storeAction: { type: String, required: false },
         filters: { type: Object, required: false },
         loadData: { type: Boolean, default: false },
         loading: { type: Boolean, default: false },
         showCheckbox: { type: Boolean, default: false },

     })

const showCheckbox = computed(() => !!props.showCheckbox)

// itemText aceita string (nome da propriedade) ou funcao (item) => texto -
// v-autocomplete ja suporta os dois nativamente via :item-text, mas os
// slots customizados abaixo (checkbox e selection) indexam manualmente.
function getItemText(item) {
    if (!item || !props.itemText) return ''
    return typeof props.itemText === 'function' ? props.itemText(item) : item[props.itemText]
}

//Data
const textSearch = ref('')
const isLoading = ref(false)
const hideData = ref(true)


//Vuex
const store = useStore()
const ActionRequest = (params) => store.dispatch(props.storeAction, params)

const model = computed({
    get() {
        return props.value
    },
    set(val) {
        emit('change', val)
        emit('input', val)
        emit('update:item', val)
    }  
})

const localCollection = ref([])

function normalizeToArray(val) {
    if (val === null || val === undefined) return []
    return Array.isArray(val) ? val : [val]
}

function extractResponseList(res) {
    // Padroniza para array: alguns endpoints retornam { data: [...] }.
    if (!res) return []
    const data = res.data
    if (Array.isArray(data)) return data
    if (data && Array.isArray(data.data)) return data.data
    return []
}

function keyOfItem(item) {
    const k = item && Object.prototype.hasOwnProperty.call(item, props.itemValue) ? item[props.itemValue] : null
    if (k === null || k === undefined) return null
    return String(k)
}

function mergeByItemValue(existing, incoming) {
    const out = []
    const indexByKey = new Map()

    const addOrReplace = (it) => {
        if (!it) return
        const k = keyOfItem(it)
        if (!k) {
            out.push(it)
            return
        }

        if (indexByKey.has(k)) {
            out[indexByKey.get(k)] = it
            return
        }

        indexByKey.set(k, out.length)
        out.push(it)
    }

    ;(Array.isArray(existing) ? existing : []).forEach(addOrReplace)
    ;(Array.isArray(incoming) ? incoming : []).forEach(addOrReplace)
    return out
}

const isEnsuringSelected = ref(false)
const lastEnsuredSignature = ref(null)

async function ensureSelectedItemsLoaded() {
    if (!props.storeAction) return
    if (props.returnObject) return

    const selected = normalizeToArray(props.value)
        .map((v) => {
            if (v === null || v === undefined) return null
            // Se vier objeto (uso indevido), tenta extrair o valor.
            if (typeof v === 'object') {
                const k = v && Object.prototype.hasOwnProperty.call(v, props.itemValue) ? v[props.itemValue] : null
                return k != null ? k : null
            }
            return v
        })
        .filter((v) => v !== null && v !== undefined && String(v).trim() !== '')

    if (!selected.length) return

    const keys = selected.map((v) => String(v))
    const signature = `${props.storeAction}::${props.itemValue}::${keys.sort().join(',')}`
    if (lastEnsuredSignature.value === signature) return

    const existingKeys = new Set((localCollection.value || []).map(keyOfItem).filter((k) => k))
    const missing = keys.filter((k) => !existingKeys.has(k))
    if (!missing.length) {
        lastEnsuredSignature.value = signature
        return
    }

    if (isEnsuringSelected.value) return
    isEnsuringSelected.value = true
    try {
        const baseFilters = props.filters || {}
        const baseFindColumns = baseFilters.find_columns || {}
        const find_columns = { ...baseFindColumns, [props.itemValue]: missing.join(',') }

        const params = {
            columns: props.columns,
            column_order: props.columnOrder,
            with: props.with,
            per_page: Math.max(missing.length, 50),
            ...baseFilters,
            find_columns,
        }

        const res = await ActionRequest(params)
        const list = extractResponseList(res)
        if (list.length) {
            localCollection.value = mergeByItemValue(localCollection.value, list)
        }
        lastEnsuredSignature.value = signature
    } finally {
        isEnsuringSelected.value = false
    }
}

watch(localCollection, (value) => {

    if(!value) return

    if(value.length === 0) return
    emit('onShowFormCreate', false)
})

watch(() => props.item, (value) => {

    if(value === undefined) return

    // Quando usado apenas com lista local (sem storeAction),
    // nao faz merge para evitar duplicacao a cada update do parent.
    if (!props.storeAction) {
        if (value === null) {
            model.value = null
            localCollection.value = []
            return
        }

        if (value === undefined) return

        if (!Array.isArray(value)) {
            localCollection.value = value != null ? [value] : []
        } else {
            localCollection.value = [...value]
        }
        return
    }

    if(!Array.isArray(value)) {

        if(value === null) {
            model.value = null
            return
        }
        
        localCollection.value = [ 
            ...localCollection.value, 
            value
         ]    
        
    } else {
        localCollection.value = [ ...value, ...localCollection.value ]
    }
    
})

function getData() {

    // Permite uso somente com lista local (props.item) sem store-action.
    if (!props.storeAction) {
        return
    }

     const params = {
          search: textSearch.value,
          columns: props.columns,
          column_order: props.columnOrder,
          with: props.with,
         ...props.filters
       }

     // Se per_page estiver definido, força paginação para evitar endpoints que
     // fazem get() quando 'page' não existe (pode estourar memória).
     if (params.page === undefined && params.per_page !== undefined) params.page = 1

    // Evita acumular resultados de buscas anteriores, mas preserva itens
    // que precisamos para renderizar selecoes atuais.
    const selectedKeys = new Set(
        normalizeToArray(props.value)
            .map((v) => {
                if (v === null || v === undefined) return null
                if (typeof v === 'object') {
                    const k = v && Object.prototype.hasOwnProperty.call(v, props.itemValue) ? v[props.itemValue] : null
                    return k != null ? String(k) : null
                }
                return String(v)
            })
            .filter((k) => k && String(k).trim() !== '')
    )

    const pinnedFromProp = normalizeToArray(props.item)
    const pinnedFromCurrent = (localCollection.value || []).filter((it) => {
        const k = keyOfItem(it)
        return k && selectedKeys.has(k)
    })
    localCollection.value = mergeByItemValue(pinnedFromCurrent, pinnedFromProp)

    isLoading.value = true
    hideData.value = true
    ActionRequest(params)
        .then(res => {

            const list = extractResponseList(res)
            localCollection.value = mergeByItemValue(localCollection.value, list)
        })
        .finally(() => {
            isLoading.value = false

            if(localCollection.value.length === 0 && textSearch.value !== null) {
                hideData.value = false
            }
        })
    }

function openDialogCreate() {
    localCollection.value = []
    model.value = null
    emit('onShowFormCreate', true)
}

function onBlur() {
    emit('blur')
}

function onUpdateMenu(val) {
    // Quando o menu for fechado, dispara um evento específico
    if(!val) {
        emit('menu-closed')
    }
}

const debouncedSearch = debounce((newVal, oldVal) => {

     if (!props.storeAction) return
     if(!oldVal) return
     if (oldVal.length === 0) return
     
     getData()
 }, 500)

const iconSelectAll = () => {
    if (model.value.length === localCollection.value.length) return 'mdi-close-box'
    if (model.value.length > 0 && !(model.value.length === localCollection.value.length)) return 'mdi-minus-box'
    return 'mdi-checkbox-blank-outline'
}

const toggleSelectAll = () => {
    if (localCollection.value.length === model.value.length) {
        model.value = [...[]]
    } else {
        // Quando return-object=false (padrao), o v-autocomplete espera IDs (item-value)
        // e nao os objetos completos.
        if (props.returnObject) {
            model.value = [...localCollection.value.slice()]
        } else {
            model.value = localCollection.value
                .map((it) => (it && Object.prototype.hasOwnProperty.call(it, props.itemValue) ? it[props.itemValue] : null))
                .filter((v) => v !== null && v !== undefined)
        }
    }
}

// Watchers
watch(textSearch, (newVal, oldVal) => {
  debouncedSearch(newVal, oldVal)
})

// Snapshot do último estado de filters pra evitar refetch quando a referência
// muda mas o conteúdo é o mesmo (caso comum quando o pai cria objetos inline
// no template, ou quando Vuetify dispara re-renders sem mudança real). Inicia
// já com o valor de montagem para não duplicar o fetch do onMounted() abaixo
// quando o watcher dispara logo em seguida com o mesmo conteúdo.
let lastFiltersSignature = (() => {
    try { return JSON.stringify(props.filters || {}) } catch (e) { return '' }
})()
watch(() => props.filters, (newVal) => {
    if (!props.storeAction) return
    let signature
    try { signature = JSON.stringify(newVal || {}) } catch (e) { signature = String(Date.now()) }
    if (signature === lastFiltersSignature) return
    lastFiltersSignature = signature
    getData()
}, { deep: true })

//Define se o carregamento da lista é automático
onMounted(() => {

    //Verifica se existe item
    if(props.item !== undefined && props.item !== null) {

        if(!Array.isArray(props.item)) {
            localCollection.value = [ props.item ]    
        } else {
            localCollection.value.push(...props.item)
        }
    }

    if (props.loadData && props.storeAction) {
        getData()
    }

    // Em modo id(s) (return-object=false), garante que os itens selecionados
    // existam em `items` para o Vuetify conseguir renderizar o label ao reabrir a tela.
    ensureSelectedItemsLoaded()

})

watch(
    () => props.value,
    () => {
        ensureSelectedItemsLoaded()
    },
    { deep: true }
)

onBeforeUpdate(() => {

    if(model.value === null || model.value === undefined) { 
        model.value = null
        
        if(!props.loadData && (props.item === undefined || props.item === null) && !textSearch.value) {
            localCollection.value = []
        }
    }

})


</script>
